import React from 'react'
import { WithApolloClient, withApollo, FetchResult } from 'react-apollo'
import {
  PathValidationErrorFragment,
  PathFlowFragment,
  PathFlowPageFragment,
  IdPathFlowQuestionType,
  ListPathFlowQuestionType,
  InterstitialPathFlowPageMetadataType
} from '#src/generated/graphql'
import { withUserAgent, WithUserAgentProps } from '#src/containers/UserAgent'
import {
  getPathFlowCategorySlug,
  extractPageInterstitialData,
  getPreviousNodeFromPage
} from '#src/services/path'
import { isMobileNative } from '#src/services/mobile'
import { withAnalytics, WithAnalyticsProps } from '#src/containers/Analytics'
import * as PathLayouts from '../Layouts'

import { FormContextProvider } from '../FormContext'
import { PathLayouts as PathLayoutTypes } from '#src/typings'

import SubmitInputTextPathFlowQuestionDocument from '#src/components/Path/mutations/SubmitInputTextPathFlowQuestion.gql'
import SubmitListPathFlowQuestionDocument from '#src/components/Path/mutations/SubmitListPathFlowQuestion.gql'
import SubmitOAuthEmailPathFlowQuestionDocument from '#src/components/Path/mutations/SubmitOAuthEmailPathFlowQuestion.gql'
import SubmitNumericPathFlowQuestionDocument from '#src/components/Path/mutations/SubmitNumericPathFlowQuestion.gql'
import SubmitIdPathFlowQuestionDocument from '#src/components/Path/mutations/SubmitIdPathFlowQuestion.gql'
import SubmitDateRangePathFlowQuestionDocument from '#src/components/Path/mutations/SubmitDateRangePathFlowQuestion.gql'
import SubmitDatePathFlowQuestionDocument from '#src/components/Path/mutations/SubmitDatePathFlowQuestion.gql'

interface FormProps {
  pathFlow: PathFlowFragment
  pathFlowPage: PathFlowPageFragment
  isPageSubmitting: boolean
  isPageLoading: boolean
  onMobileNativePageBack: () => void
  previousPageSlugs: string[]
  previousPathSlugs: string[]
  onUpdateScrollRef: (scrollRef: HTMLDivElement | Window) => void
  onPageSubmitted: () => void
  onPageBack: () => void
  pathLayout?: PathLayoutTypes
}

type QuestionSubmissionResponse = FetchResult<{}, Record<string, any>>

interface QuestionTrackingSegment {
  attribute_name: string
  value: FormValueType
  parent_category_slug: string
}

interface ValidFormQuestionsState {
  [questionName: string]: {
    valid: boolean
  }
}

export type FormValueType = number | string | string[] | Date | null | undefined

export type FormValueObject = { [key: string]: FormValueType }

export interface ValuesState {
  [questionName: string]: FormValueObject
}

interface FormState {
  errors: PathValidationErrorFragment[]
  values: ValuesState
  validated: ValidFormQuestionsState
  isFormSubmitting: boolean
}

class Form extends React.Component<
  FormProps & WithApolloClient<{}> & WithAnalyticsProps & WithUserAgentProps,
  FormState
> {
  public mutations: { [key: string]: (value: any) => any } = {}

  public trackingData: QuestionTrackingSegment[] = []

  constructor(props: FormProps & WithApolloClient<{}> & WithAnalyticsProps & WithUserAgentProps) {
    super(props)

    this.mutations = {}

    this.state = {
      errors: [],
      values: {},
      validated: {},
      isFormSubmitting: false
    }

    this.handleFieldValidityChanged = this.handleFieldValidityChanged.bind(this)
    this.handleFieldValueChanged = this.handleFieldValueChanged.bind(this)
    this.handlePageSubmit = this.handlePageSubmit.bind(this)
    this.handleSubmit = this.handleSubmit.bind(this)
    this.handleBack = this.handleBack.bind(this)
    this.isFormValid = this.isFormValid.bind(this)
  }

  public componentDidUpdate() {
    /**
     * If the state contains errors from a different page, wipe them
     */
    if (this.state.errors.length > 0) {
      const pageWithErrors = this.state.errors.find(
        error => error.pageName === this.props.pathFlowPage.slug
      )

      if (!pageWithErrors) {
        this.setState({
          errors: []
        })
      }
    }
  }

  public isBackButtonHidden() {
    const {
      pathFlow,
      pathFlowPage: { slug: currentPageSlug },
      previousPageSlugs,
      previousPathSlugs
    } = this.props
    const firstPageNode = pathFlow ? pathFlow.pagesConnection.edges[0].node : null

    // The mobile native back button is always visible
    if (isMobileNative(this.props.userAgent)) {
      return false
    }

    // There are no previous pages or previous paths there is nothing to go back to
    if (previousPageSlugs.length === 0 && previousPathSlugs.length === 0) {
      return true
    }

    // Otherwise we hide the back button if its the beginning of the path
    if (firstPageNode && firstPageNode.slug === currentPageSlug) {
      return true
    }

    // We also hide the back button if all the previous pages are interstitials
    const edges = pathFlow.pagesConnection.edges
    const currentEdgeIndex = edges.findIndex(edge => edge.node.slug === currentPageSlug)
    const previousEdges = pathFlow.pagesConnection.edges.slice(0, currentEdgeIndex)
    const areAlPreviousPagesInterstitials = previousEdges.every(
      previousEdge => extractPageInterstitialData(previousEdge.node.metadata) !== null
    )

    if (areAlPreviousPagesInterstitials) {
      return true
    }

    return false
  }

  /**
   * If all submit hidden conditions are met, the submit button isn't visible
   */
  public isSubmitButtonHidden() {
    const { pathFlowPage } = this.props

    const submitHiddenConditions = [
      // Every question is a single select question and it hasn't been previously answered (we're not moving backward)
      () =>
        pathFlowPage.questions.every(question => {
          if (
            question.__typename === 'ListPathFlowQuestion' &&
            (!question.listUserInput.value || question.listUserInput.value.length === 0)
          ) {
            return (
              question.listType === ListPathFlowQuestionType.SINGLE_SELECT_LIST && question.required
            )
          }

          return false
        }),
      // Every question is a expert id question, likely the only one on the page
      () =>
        pathFlowPage.questions.every(question => {
          if (question.__typename === 'IdPathFlowQuestion') {
            return question.idType === IdPathFlowQuestionType.EXPERT_ID_SELECTION
          }

          return false
        }),
      // Is it a flight plan ID question, it has it's own submit button
      () => {
        if (pathFlowPage.questions.length === 1) {
          const maybeIdQuestion = pathFlowPage.questions[0]

          if (
            maybeIdQuestion.__typename === 'IdPathFlowQuestion' &&
            maybeIdQuestion.idType === IdPathFlowQuestionType.FLIGHT_PLAN
          ) {
            return true
          }
        }

        return false
      }
    ]

    return submitHiddenConditions.some(condition => condition())
  }

  /**
   * Are all the questions on the page optional
   */
  public areAllQuestionsOptional() {
    return this.props.pathFlowPage.questions.every(question => !question.required)
  }

  /**
   * Do all the questions have a representation in state
   */
  public areAllQuestionsAnswered() {
    return this.props.pathFlowPage.questions.every(question => {
      return this.state.values[question.internalName] !== undefined
    })
  }

  public handleResetState(callback: any) {
    this.setState(
      {
        errors: [],
        values: {},
        validated: {},
        isFormSubmitting: false
      },
      callback()
    )
  }

  /**
   * Wipe the state to prevent lingering values in the form as we move backward
   */
  public handleBack() {
    /**
     * HACK HACK to handle going back within the mobile native application
     */
    if (isMobileNative(this.props.userAgent)) {
      const { pathFlow, pathFlowPage } = this.props

      const prevNode = getPreviousNodeFromPage(pathFlow, pathFlowPage.slug)
      const prevNodeInterstital = prevNode ? extractPageInterstitialData(prevNode.metadata) : null
      const skippableTypes = [
        InterstitialPathFlowPageMetadataType.GENERIC_WITH_TITLE,
        InterstitialPathFlowPageMetadataType.REQUEST_PUSH_NOTIFICATION_PERMISSION
      ]

      /**
       * If the previous node doesn't exist OR the previous node is an interstitial with no back button use mobile native back method
       */
      if (
        !prevNode ||
        (prevNodeInterstital && skippableTypes.includes(prevNodeInterstital.interstitialType))
      ) {
        this.handleResetState(() => this.props.onMobileNativePageBack())
        return
      }
    }

    this.handleResetState(() => this.props.onPageBack())
  }

  /**
   * Reset the form by clearing errors and submit the page
   */
  public handleSubmit(ev?: React.ChangeEvent<HTMLFormElement>) {
    if (ev) ev.preventDefault()

    if (this.state.isFormSubmitting || this.props.isPageSubmitting) {
      return false
    }

    this.setState(
      {
        errors: [],
        isFormSubmitting: true
      },
      this.handlePageSubmit
    )
  }

  /**
   * Submit all of the mutations for each question
   */
  public handlePageSubmit() {
    const { pathFlow, pathFlowPage } = this.props
    const values = this.state.values
    const interstitialMetaData = extractPageInterstitialData(pathFlowPage.metadata)

    /**
     * Pages with interstitials can't have questions, skip the mutation submission
     */
    if (interstitialMetaData) {
      return this.handleResetState(this.props.onPageSubmitted)
    }

    /**
     * Build the mutations object off the questions
     */
    const mutations = pathFlowPage.questions.map(question => {
      const mutationVariables = {
        questionName: question.internalName,
        pathFlowId: pathFlow.id,
        ...values[question.internalName]
      }

      let mutationDocument

      switch (question.__typename) {
        case 'ListPathFlowQuestion':
          mutationDocument = SubmitListPathFlowQuestionDocument
          break

        case 'InputTextPathFlowQuestion':
          mutationDocument = SubmitInputTextPathFlowQuestionDocument
          break

        case 'NumericPathFlowQuestion':
          mutationDocument = SubmitNumericPathFlowQuestionDocument
          break

        case 'OAuthEmailPathFlowQuestion':
          mutationDocument = SubmitOAuthEmailPathFlowQuestionDocument
          break

        case 'IdPathFlowQuestion':
          mutationDocument = SubmitIdPathFlowQuestionDocument
          break

        case 'DatePathFlowQuestion':
          mutationDocument = SubmitDatePathFlowQuestionDocument
          break

        case 'DateRangePathFlowQuestion':
          mutationDocument = SubmitDateRangePathFlowQuestionDocument
          break

        default:
          throw new Error(`Unexpected question type passed`)
      }

      return this.props.client.mutate({
        variables: { input: mutationVariables },
        mutation: mutationDocument
      })
    })

    /**
     * Loop all the questions in the mutations object and
     * assign associated submit functions to them
     */
    this.trackingData = pathFlowPage.questions.map(question => {
      const questionName = question.internalName
      const questionValue = values[questionName]

      return {
        attribute_name: questionName,
        parent_category_slug: getPathFlowCategorySlug(pathFlow),
        category_slug: getPathFlowCategorySlug(pathFlow),
        value: questionValue ? questionValue.value : undefined
      }
    })

    /**
     * Fire all the question mutations
     */
    Promise.all(mutations).then((results: QuestionSubmissionResponse[]) => {
      let errors: PathValidationErrorFragment[] = []

      results.forEach((result: QuestionSubmissionResponse) => {
        const { data } = result

        if (data) {
          for (let n in data) {
            // Typed as any because this is a union of all question types
            const fieldErrors = (data as any)[n].errors

            if (fieldErrors.length > 0) {
              errors.push(...fieldErrors)
            }
          }
        }
      })

      this.setState(
        {
          errors,
          isFormSubmitting: false
        },
        () => {
          /**
           * Only submit the page if there were no field level errors
           */
          if (this.state.errors.length === 0) {
            this.trackingData.forEach(trackingSegment => {
              if (this.props.analytics) {
                this.props.analytics.pathQuestionSubmitted(trackingSegment)
              }
            })

            /**
             * Clean up the mutations and tracking and
             * Call the prop to submit the entire page
             */
            this.handleResetState(this.props.onPageSubmitted)
          }
        }
      )
    })
  }

  /**
   * On a field's value change, update the state with that value
   */
  public handleFieldValueChanged(name: string, value: FormValueObject, callback?: () => void) {
    this.setState(prevState => {
      // Make sure to merge the existing values for single questions that supply multiple properties
      const previousValueReference: FormValueObject = prevState.values[name]
      const valueReference = previousValueReference
        ? { ...previousValueReference, ...value }
        : value

      return {
        values: {
          ...prevState.values,
          [name]: valueReference
        }
      }
    }, callback)
  }

  /**
   * On a field's change from invalid to valid, update the state
   */
  public handleFieldValidityChanged(name: string, valid: boolean) {
    this.setState(prevState => {
      return {
        validated: {
          ...prevState.validated,
          [name]: {
            valid
          }
        }
      }
    })
  }

  /**
   * Is the entire form valid or not
   */
  public isFormValid() {
    const { pathFlowPage } = this.props

    // If all questions are optional, the form is always valid
    if (this.areAllQuestionsOptional()) {
      return true
    }

    return pathFlowPage.questions.every(question => {
      const questionInState = this.state.validated[question.internalName]

      return questionInState && questionInState.valid
    })
  }

  /**
   * Get the path layout from the prop or default to baseline
   */
  public handleGetPathLayout(pathLayout?: PathLayoutTypes) {
    switch (pathLayout) {
      case PathLayoutTypes.BUBBA:
        return PathLayouts.Bubba

      case PathLayoutTypes.BROOKS:
        return PathLayouts.Brooks

      case PathLayoutTypes.MODAL:
        return PathLayouts.Modal

      case PathLayoutTypes.WOODS:
        return PathLayouts.Woods

      case PathLayoutTypes.RORY:
        return PathLayouts.Rory

      case PathLayoutTypes.DEFAULT:
      default:
        return PathLayouts.Default
    }
  }

  public render() {
    const { pathFlow, pathFlowPage, pathLayout } = this.props
    const PathLayout = this.handleGetPathLayout(pathLayout)

    return (
      <FormContextProvider
        value={{
          pathFlow,
          isFormSubmitting: this.state.isFormSubmitting,
          isFormValid: this.isFormValid(),
          onPageSubmitted: this.handleSubmit,
          onFieldValidityChanged: this.handleFieldValidityChanged,
          onFieldValueChanged: this.handleFieldValueChanged
        }}
      >
        <PathLayout
          pathFlowPage={pathFlowPage}
          onUpdateScrollRef={this.props.onUpdateScrollRef}
          onPageBack={this.handleBack}
          isBackButtonHidden={this.isBackButtonHidden()}
          isSubmitButtonHidden={this.isSubmitButtonHidden()}
          isPageSubmitting={this.props.isPageSubmitting}
          isPageLoading={this.props.isPageLoading}
          areAllQuestionsOptional={this.areAllQuestionsOptional()}
          areAllQuestionsAnswered={this.areAllQuestionsAnswered()}
          errors={this.state.errors}
          questions={pathFlowPage.questions}
          questionValues={this.state.values}
        />
      </FormContextProvider>
    )
  }
}

export default withAnalytics(withApollo(withUserAgent(Form)))
